import { Link } from "react-router-dom";
import { ShieldCheck, CheckCircle2, Sparkles, ArrowRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { servicesData } from "../lib/servicesData";

// Reuses the same data as the /servicos/seguranca-cctv page so the home
// "CCTV" anchor doesn't dead-end on the comparator alone — visitors that
// click the floating quick-nav (or the bottom-of-page CTAs) now see the
// actual equipment photos + capability list right below the comparator.

const COPY = {
  pt: {
    overline: "Equipamentos CCTV",
    title: "Câmaras, NVRs e analítica IA que instalamos em Angola",
    body: "Câmaras analógicas e IP de alta resolução, reconhecimento facial, leitura automática de matrículas e gravação contínua em NVR/DVR — integráveis com alarme, controlo de acessos e cerca eléctrica.",
    capabilities_label: "Capacidades-chave",
    highlights_label: "Resultados práticos",
    full_page_cta: "Ver página completa",
    quote_cta: "Pedir orçamento",
  },
  en: {
    overline: "CCTV equipment",
    title: "Cameras, NVRs and AI analytics we install in Angola",
    body: "High-resolution analog and IP cameras, facial recognition, automatic licence-plate reading and continuous NVR/DVR recording — integrable with alarms, access control and electric fences.",
    capabilities_label: "Core capabilities",
    highlights_label: "Practical outcomes",
    full_page_cta: "See the full page",
    quote_cta: "Request a quote",
  },
  es: {
    overline: "Equipos CCTV",
    title: "Cámaras, NVRs y analítica IA que instalamos en Angola",
    body: "Cámaras analógicas e IP de alta resolución, reconocimiento facial, lectura automática de matrículas y grabación continua en NVR/DVR — integrables con alarma, control de accesos y cerca eléctrica.",
    capabilities_label: "Capacidades clave",
    highlights_label: "Resultados prácticos",
    full_page_cta: "Ver página completa",
    quote_cta: "Solicitar presupuesto",
  },
};

export const CctvGallery = () => {
  const { lang } = useLang();
  const copy = COPY[lang] || COPY.pt;
  const data = servicesData["seguranca-cctv"];
  if (!data) return null;
  const items = data.items[lang] || data.items.pt;
  const highlights = data.highlights[lang] || data.highlights.pt;

  return (
    <section
      id="cctv-equipment"
      data-testid="cctv-equipment"
      className="relative scroll-mt-24 border-t border-slate-200 bg-gradient-to-b from-slate-900 via-[#0a1730] to-slate-900 py-20 text-white lg:py-24"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        {/* Header */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.28em] text-cyan-300">
              <ShieldCheck className="h-3.5 w-3.5" strokeWidth={2.5} />
              {copy.overline}
            </div>
            <h2
              data-testid="cctv-equipment-title"
              className="font-heading mt-4 text-3xl font-black tracking-tight text-white md:text-4xl lg:text-5xl"
            >
              {copy.title}
            </h2>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-300 md:text-base">
              {copy.body}
            </p>
          </div>
          <div className="flex items-end lg:col-span-5 lg:justify-end">
            <div className="flex flex-wrap gap-3">
              <Link
                to="/servicos/seguranca-cctv"
                data-testid="cctv-equipment-full-page-cta"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-5 py-2.5 text-sm font-semibold text-white backdrop-blur transition-all hover:-translate-y-0.5 hover:border-cyan-400/60 hover:bg-white/10"
              >
                {copy.full_page_cta}
                <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
              </Link>
              <a
                href="/#contact"
                data-testid="cctv-equipment-quote-cta"
                className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2.5 text-sm font-bold text-slate-900 shadow-md transition-all hover:-translate-y-0.5 hover:bg-cyan-300"
              >
                {copy.quote_cta}
                <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
              </a>
            </div>
          </div>
        </div>

        {/* Photo gallery — mosaic, hero spans 2x2 */}
        <div
          data-testid="cctv-equipment-gallery"
          className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
        >
          {data.gallery.map((src, i) => (
            <div
              key={src}
              className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-slate-900/40 shadow-xl ${
                i === 0 ? "sm:col-span-2 sm:row-span-2" : ""
              }`}
            >
              <div className="relative aspect-[4/3]">
                <img
                  src={src}
                  alt=""
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/10 to-transparent" />
                <div className="pointer-events-none absolute left-4 top-4 rounded-full border border-white/20 bg-black/45 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-white backdrop-blur">
                  {String(i + 1).padStart(2, "0")} · CCTV
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Capabilities + Highlights two-column */}
        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
          <div className="rounded-3xl border border-white/10 bg-slate-900/60 p-6 backdrop-blur-sm md:p-8 lg:col-span-7">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-300/80">
              {copy.capabilities_label}
            </div>
            <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {items.map((it) => (
                <li
                  key={it}
                  className="flex items-start gap-2.5 rounded-xl border border-white/10 bg-white/5 p-3.5 text-[13px] leading-snug text-slate-100"
                >
                  <CheckCircle2
                    className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300"
                    strokeWidth={2.5}
                  />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl border border-cyan-400/20 bg-gradient-to-br from-cyan-950/30 via-slate-900/60 to-slate-900/60 p-6 backdrop-blur-sm md:p-8 lg:col-span-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-300/80">
              {copy.highlights_label}
            </div>
            <ul className="mt-4 space-y-3">
              {highlights.map((h) => (
                <li
                  key={h}
                  className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-100"
                >
                  <Sparkles
                    className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300"
                    strokeWidth={2.5}
                  />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CctvGallery;
